/**
 * What makes one civilisation play unlike another (ADR-0062): its two traits and its
 * unique unit.
 *
 * Traits are numbers in traits.json read here and nowhere else; the systems they touch
 * (costs, science, gold, food, promotion, ships' movement, revolutions) ask this module.
 * A unique unit stands in for a standard one for its civilisation alone: the standard
 * one is never offered to that civilisation, the unique one never to anybody else.
 *
 * Imports only content and state types, so anything may ask it.
 */

import type { CivilizationDef, Content, TraitDef, UnitDef } from '../../../content/schema/index.js';
import type { Civ, ProductionItem } from '../state/types.js';

export function civDef(content: Content, civ: Civ): CivilizationDef {
  return content.civilizations.get(civ.civType);
}

export function traitsOf(content: Content, civ: Civ | undefined): TraitDef[] {
  if (!civ) return [];
  return civDef(content, civ).traitIds.map((id) => content.traits.get(id));
}

type Summed = 'sciencePercent' | 'goldPerCity' | 'centreFood' | 'promotionPercent' | 'seaMovement' | 'anarchyTurnsLess';

/** One numeric trait effect, summed over the civilisation's traits. */
export function traitSum(content: Content, civ: Civ | undefined, effect: Summed): number {
  let total = 0;
  for (const trait of traitsOf(content, civ)) total += trait.effects[effect];
  return total;
}

/** The percentage taken off this item's cost for this civilisation: its best discount. */
export function discountFor(content: Content, civ: Civ | undefined, item: ProductionItem): number {
  let best = 0;
  for (const trait of traitsOf(content, civ)) {
    const key = item.kind === 'unit' ? content.units.get(item.type).key : content.buildings.get(item.building).key;
    const table = item.kind === 'unit' ? trait.effects.unitDiscount : trait.effects.buildingDiscount;
    best = Math.max(best, table[key] ?? 0);
  }
  return best;
}

/**
 * The unit this civilisation builds in place of `def`: its unique unit if that replaces
 * `def`, otherwise `def` itself.
 */
export function unitFor(content: Content, civ: Civ | undefined, def: UnitDef): UnitDef {
  if (!civ) return def;
  const unique = civDef(content, civ).uniqueUnitId;
  if (unique === -1) return def;
  const u = content.units.get(unique);
  return u.replacesId === def.id ? u : def;
}

/** Whether this civilisation may ever build `def`: not another's unique, not one its own replaces. */
export function unitAvailable(content: Content, civ: Civ | undefined, def: UnitDef): boolean {
  if (def.civId !== -1) return !!civ && def.civId === civ.civType;
  return unitFor(content, civ, def) === def;
}
